import { BasicDeal } from '../../../../types/deal.types';
import {
  RatingEnhancementFilterModel,
  PredefinedEnhancementFilterValue
} from './ratings-filters';
import { FilterResultFacade } from '../../../../helpers/filters/filter-result-facade';
import { AdvancedFilterName } from '../../../../helpers/filters/filter-types';

abstract class EnhancementDealLevelFilterStrategy {
  protected filterResult: FilterResultFacade;

  public abstract apply(deal: BasicDeal): boolean;

  public config(filterResult: FilterResultFacade): void {
    this.filterResult = filterResult;
  }
}

class AllFilterDealLevelStrategy extends EnhancementDealLevelFilterStrategy {
  apply(deal: BasicDeal) {
    return true;
  }
}

class AtLeastOneSeriesEnhancedAppropriatelyStrategy extends EnhancementDealLevelFilterStrategy {
  apply(deal: BasicDeal) {
    return (
      this.someSeriesEnhanced(deal.id) || this.someMaturityEnhanced(deal.id)
    );
  }

  private someSeriesEnhanced(dealId: string) {
    return this.filterResult.someSeriesPassedFilter(
      dealId,
      AdvancedFilterName.seriesEnhancement
    );
  }

  private someMaturityEnhanced(dealId: string) {
    return this.filterResult.someMaturitiesPassedFilter(
      dealId,
      AdvancedFilterName.maturityEnhancement
    );
  }
}

class UnenhancedFilterDealLevelStrategy extends EnhancementDealLevelFilterStrategy {
  apply(deal: BasicDeal) {
    return (
      this.everySeriesUnenhanced(deal.id) &&
      this.everyMaturityUnenhanced(deal.id)
    );
  }

  private everySeriesUnenhanced(dealId: string) {
    return this.filterResult.everySeriesPassedFilter(
      dealId,
      AdvancedFilterName.seriesEnhancement
    );
  }

  private everyMaturityUnenhanced(dealId: string) {
    return this.filterResult.everyMaturityPassedFilter(
      dealId,
      AdvancedFilterName.maturityEnhancement
    );
  }
}

export class EnhancementDealLevelFilter {
  private isFilterActive: boolean;
  private stateStrategies = {
    [PredefinedEnhancementFilterValue.all]: new AllFilterDealLevelStrategy(),
    [PredefinedEnhancementFilterValue.enhanced]: new AtLeastOneSeriesEnhancedAppropriatelyStrategy(),
    [PredefinedEnhancementFilterValue.unenhanced]: new UnenhancedFilterDealLevelStrategy(),
    specificInsurer: new AtLeastOneSeriesEnhancedAppropriatelyStrategy()
  };

  private selectedStrategy: EnhancementDealLevelFilterStrategy;

  constructor(
    model: RatingEnhancementFilterModel,
    filterResult: FilterResultFacade
  ) {
    this.isFilterActive = !!model && model.isFilterActive && !!model.radio;

    if (this.isFilterActive) {
      this.selectedStrategy =
        this.stateStrategies[model.radio] ||
        this.stateStrategies.specificInsurer;

      this.selectedStrategy.config(filterResult);
    }
  }

  applyFilter(deal: BasicDeal): boolean {
    if (!this.isFilterActive) {
      return true;
    }
    return this.selectedStrategy.apply(deal);
  }
}
